// @ts-nocheck
import React, { PureComponent } from 'react'
import history from 'helpers/history'
import {
    getUpdatedList, handleRenderColumn, isValidDate, onColumnClick,
} from 'helpers/methods/common'
import Status from 'types/status'
import InvalidEntityError from 'requests/errors/invalidEntityError'
import CancelRequestError from 'requests/errors/cancelRequestError'
import NotImplementedError from 'requests/errors/notImplementedError'
import UnauthorizedError from 'requests/errors/unauthorizedError'
import flattenObj from 'helpers/methods/flattenObj'
import time from 'helpers/methods/time'
import styles from 'styles/pages/transports/index.module.scss'
import { tKey, tObj } from 'helpers/methods/translate'
import {
    Text, Icon, Label, DefaultButton, DirectionalHint, TooltipDelay, TooltipHost, TextField,
} from '@fluentui/react'
import { Columns } from 'react-bulma-components'
import parseJson from 'helpers/methods/parseJson'
import Card from 'components/containers/card'
import FilteredVirtualCombobox from 'components/inputs/filteredVirtualCombobox'
import TransportList from 'components/pages/transports/transportList'
import Transport from 'requests/objects/transport'
import ContentResponsive from 'components/containers/contentResponsive'
import TransportListCard from 'components/pages/transports/transportListCard'
import TransportLabelModal from 'components/pages/transports/transportLabelModal'
import Pagination from 'components/pages/Pagination'
import HandleBlob from 'helpers/methods/blob'
import DatePicker from 'components/inputs/datePicker'

const DateFilterMinDate = new Date(1970, 0, 1)
const DateFilterMaxDate = new Date(2100, 11, 31)

// Only prefill the default label count when every selected transport belongs to the same client.
// Note: items go through flattenObj(), so the client relation is exposed as 'client.labelFormat', not a nested object.
export const getDefaultLabelFormat = selectedTransports => {
    const selectedClientIds = [...new Set(selectedTransports.map(transport => transport.clientId))]

    return selectedClientIds.length === 1 ? selectedTransports[0]['client.labelFormat'] : null
}

/** @debug {import('app').AppProps} */
/**
 * @typedef {object} SearchParamsType
 * @property {number | null} clientId Selected client id (admin only).
 * @property {string} references Transport references search text.
 * @property {string} articleReferences Article references search text.
 * @property {Date | null} pickupDate Pickup date range start.
 * @property {Date | null} pickupDateEnd Pickup date range end.
 * @property {string} pickupAddress Pickup address search text.
 * @property {number[]} workflowTransportStepIds Selected transport workflow step IDs.
 * @property {Date | null} creationDate Creation date range start.
 * @property {Date | null} creationDateEnd Creation date range end.
 * @property {Date | null} deliveryDate Delivery date range start.
 * @property {Date | null} deliveryDateEnd Delivery date range end.
 * @property {string} deliveryAddress Delivery address search text.
 */
/**
 * @augments {PureComponent<import('app').AppProps>} extends
 */
export default class IndexTransport extends PureComponent {
    constructor(props) {
        super(props)

        this.state = {
            /** @type {Status} Current status of the component */
            status: Status.IDLE,
            /** @type {Transport[]} Items found in API */
            items: [],
            /** @type {SearchParamsType} Params to search */
            searchParams: {
                clientId: null,
                references: '',
                articleReferences: '',
                pickupDate: null,
                pickupDateEnd: null,
                pickupAddress: '',
                workflowTransportStepIds: [],
                creationDate: null,
                creationDateEnd: null,
                deliveryDate: null,
                deliveryDateEnd: null,
                deliveryAddress: '',
            },
            /** @type {import('components/pages/Pagination').PagingationType} pagination data */
            pagination: {
                currentPage: 1,
                totalPages: 1,
                totalRecords: 0,
            },
            /** @type {boolean} Is filter card reduced ? */
            isFilterCardReduced: true,
            /** @type {Status} Current document download status */
            documentStatus: Status.IDLE,
            isTransportLabelModalVisible: false,
            selectedTransports: [],
        }

        this.submitInput = React.createRef()
        this.toolTipRef = React.createRef()

        /** @type {NodeJS.Timeout} Time out to handle apply of searchParams */
        this.timeOutParams = null

        this.export = this.export.bind(this)
        this.downloadDocuments = this.downloadDocuments.bind(this)
    }

    /**
     * @inheritdoc
     */
    componentDidMount() {
        this.init()
        this.search()
    }

    /**
     * @inheritdoc
     * @param {object} prevProps Previous Props
     * @param {object} prevState Previous State
     */
    componentDidUpdate(prevProps, prevState) {
        const { lang, me, match } = this.props
        const { searchParams, pagination: { currentPage } } = this.state

        if (JSON.stringify(prevState.searchParams) !== JSON.stringify(searchParams)) {
            // Apply changement with delay to prevent lag
            clearTimeout(this.timeOutParams)
            this.timeOutParams = setTimeout(() => {
                history.replace({ search: '' })
                this.syncSearchParamsInHistory()
            }, 250)
        }
        if (lang !== prevProps.lang)
            this.init()

        if (me.companyId !== prevProps.me.companyId)
            this.init()

        if (match.params?.key !== prevProps.match.params?.key)
            this.setState(truePevState => ({
                searchParams: {
                    ...truePevState.searchParams,
                    workflowTransportStepIds: [],
                },
            }), () => {
                this.init()
                this.search()
            })

        if (prevState?.pagination?.currentPage && prevState.pagination.currentPage !== currentPage)
            this.search()
    }

    /**
     * @inheritdoc
     */
    componentWillUnmount() {
        this.transportsHandlerGetAll?.cancel()
        this.transportsHandlerExportAll?.cancel()
        this.transportsHandlerDownloadDocuments?.cancel()
    }

    /**
     * Setup breadcrumb elements
     */
    setupBreadcrumb() {
        const { setBreadcrumb } = this.props

        setBreadcrumb([
            { text: this.tKey('orderEntry'), key: 'order' },
            { text: this.tKey('allTransports'), key: 'all-transport', isCurrentItem: true },
        ])
    }

    /**
     * Setup commandbar elements
     */
    setupCommandBar() {
        const { setCommand } = this.props

        setCommand([])
    }

    /**
     * Sync SearchParams in navigation history
     */
    syncSearchParamsInHistory() {
        const { searchParams } = this.state

        const queryParams = new URLSearchParams(window.location.search)

        // eslint-disable-next-line no-restricted-syntax
        for (const key in searchParams)
            if (searchParams[key] !== null && searchParams[key] !== '' && searchParams[key]?.length !== 0)
                if (Array.isArray(searchParams[key]))
                    // eslint-disable-next-line no-restricted-syntax
                    for (const element of searchParams[key])
                        queryParams.append(key, element)
                else
                    queryParams.set(key, searchParams[key])
            else
                queryParams.delete(key)

        history.replace({
            search: queryParams.toString(),
        })
    }

    /**
     * Init Page
     */
    init() {
        const { setMessageBar, setCommand } = this.props

        setMessageBar({ isDisplayed: false })
        setCommand([])

        this.setupBreadcrumb()
        this.setupCommandBar()

        if (!window.location.search) {
            this.syncSearchParamsInHistory()
        } else {
            const queryParams = new URLSearchParams(window.location.search)

            const pickupDateTimestamp = Date.parse(queryParams.get('pickupDate'))
            const pickupDateEndTimestamp = Date.parse(queryParams.get('pickupDateEnd'))
            const creationDateTimestamp = Date.parse(queryParams.get('creationDate'))
            const creationDateEndTimestamp = Date.parse(queryParams.get('creationDateEnd'))
            const deliveryDateTimestamp = Date.parse(queryParams.get('deliveryDate'))
            const deliveryDateEndTimestamp = Date.parse(queryParams.get('deliveryDateEnd'))

            // Reset Filter
            this.setState({
                /** @type {SearchParamsType} */
                searchParams: {
                    clientId: queryParams.get('clientId') ? +queryParams.get('clientId') : null,
                    references: queryParams.get('references') ? queryParams.get('references') : '',
                    articleReferences: queryParams.get('articleReferences') ? queryParams.get('articleReferences') : '',
                    pickupAddress: queryParams.get('pickupAddress') ? queryParams.get('pickupAddress') : '',
                    deliveryAddress: queryParams.get('deliveryAddress') ? queryParams.get('references') : '',
                    pickupDate: !Number.isNaN(pickupDateTimestamp) ? new Date(pickupDateTimestamp) : null,
                    pickupDateEnd: !Number.isNaN(pickupDateEndTimestamp) ? new Date(pickupDateEndTimestamp) : null,
                    workflowTransportStepIds: queryParams.getAll('workflowTransportStepIds') ? queryParams.getAll('workflowTransportStepIds').map(x => +x) : [],
                    creationDate: !Number.isNaN(creationDateTimestamp) ? new Date(creationDateTimestamp) : null,
                    creationDateEnd: !Number.isNaN(creationDateEndTimestamp) ? new Date(creationDateEndTimestamp) : null,
                    deliveryDate: !Number.isNaN(deliveryDateTimestamp) ? new Date(deliveryDateTimestamp) : null,
                    deliveryDateEnd: !Number.isNaN(deliveryDateEndTimestamp) ? new Date(deliveryDateEndTimestamp) : null,
                },
            }, () => this.syncSearchParamsInHistory())
        }
    }

    /**
     * Search elements
     * @param {string} orderBy orderBy
     */
    search(orderBy = undefined) {
        const { setMessageBar } = this.props
        const { orderBy: currentOrderBy } = this.state
        const nextOrderBy = orderBy ?? currentOrderBy
        setMessageBar({ isDisplayed: false })
        this.setState({
            status: Status.PENDING,
            orderBy: nextOrderBy,
        }, async () => {
            const { transportsHandler, match } = this.props
            const { searchParams, pagination: { currentPage } } = this.state
            try {
                this.transportsHandlerGetAll?.cancel()
                this.transportsHandlerGetAll = transportsHandler.getAllFromPage({
                    ...searchParams,
                    page: match.params?.key,
                    currentPage,
                    orderBy: nextOrderBy,
                })
                const res = await this.transportsHandlerGetAll.fetch()
                this.setState({
                    items: res.items.map(x => this.flattenObj(x)),
                    pagination: {
                        currentPage: res.items.length === 0 ? 1 : currentPage,
                        totalPages: res.totalPages,
                        totalRecords: res.totalRecords,
                    },
                    status: Status.RESOLVED,
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError: break
                    case NotImplementedError:
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                    default:
                        this.setState({
                            items: [],
                            status: Status.REJECTED,
                        })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Export elements
     */
    export() {
        this.setState({
            status: Status.PENDING,
        }, async () => {
            const { transportsHandler, match, lang } = this.props
            const { searchParams } = this.state
            try {
                this.transportsHandlerExportAll = transportsHandler.exportAll({
                    ...searchParams,
                    page: match.params?.key,
                    lang,
                })
                const { fileName, blob } = await this.transportsHandlerExportAll.fetch()
                HandleBlob.download(blob, fileName)
                this.setState({ status: Status.RESOLVED })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError:
                    case NotImplementedError:
                    default:
                        this.setState({ status: Status.REJECTED })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Download selected transport documents
     * @param {'summary' | 'labels'} type Document type
     * @param {number=} nbPerPage Labels per page
     */
    downloadDocuments(type, nbPerPage = 1) {
        const { selectedTransports } = this.state

        if (!selectedTransports.length)
            return

        this.setState({
            documentStatus: Status.PENDING,
        }, async () => {
            const { transportsHandler, lang } = this.props

            try {
                this.transportsHandlerDownloadDocuments?.cancel()

                this.transportsHandlerDownloadDocuments = transportsHandler.downloadDocuments({
                    transportIds: selectedTransports.map(transport => transport.transportId),
                    type,
                    lang,
                    nbPerPage,
                })
                const { fileName, blob } = await this.transportsHandlerDownloadDocuments.fetch()
                HandleBlob.download(blob, fileName)

                this.setState({
                    documentStatus: Status.RESOLVED,
                    isTransportLabelModalVisible: false,
                })
            } catch (error) {
                switch (error?.constructor) {
                    case CancelRequestError:
                    case UnauthorizedError:
                    case InvalidEntityError:
                    case NotImplementedError:
                    default:
                        this.setState({ documentStatus: Status.REJECTED })
                        // eslint-disable-next-line no-console
                        console.error(error)
                        break
                }
            }
        })
    }

    /**
     * Render component
     * @returns {JSX.Element} Element
     */
    render() {
        const {
            status,
            isFilterCardReduced,
            items,
            searchParams,
            pagination,
            selectedTransports,
            documentStatus,
            isTransportLabelModalVisible,
        } = this.state
        const {
            lang, param, match, me,
        } = this.props

        const defaultLabelFormat = getDefaultLabelFormat(selectedTransports)

        return (
            <main className={styles.index}>
                <Card
                    title={this.tKey('filters')}
                    iconName="FilterSettings"
                    headerComponent={(
                        <button
                            type="button"
                            onClick={() => this.setState({ isFilterCardReduced: !isFilterCardReduced })}
                            className={styles['reduce-button-container']}
                        >
                            <div className={styles['reduce-button']}>
                                <Text>
                                    {isFilterCardReduced ? this.tKey('reduce') : this.tKey('extend')}
                                </Text>
                                <Text>
                                    <Icon iconName={isFilterCardReduced ? 'ChevronUp' : 'ChevronDown'} />
                                </Text>
                            </div>
                        </button>
                    )}
                >
                    {isFilterCardReduced && (
                        <form
                            onSubmit={ev => {
                                ev.preventDefault()
                                this.search()
                            }}
                        >
                            <Columns>
                                <Columns.Column size="one-fifth">
                                    <TextField
                                        label={this.tKey('searchReferences')}
                                        placeholder={this.tKey('search')}
                                        disabled={status === Status.PENDING}
                                        value={searchParams.references}
                                        onChange={(ev, newVal) => this.setState({ searchParams: { ...searchParams, references: newVal } })}
                                    />
                                </Columns.Column>
                                <Columns.Column size="one-fifth">
                                    <TextField
                                        label={this.tKey('pickupAddress')}
                                        placeholder={this.tKey('pickupAddress')}
                                        value={searchParams.pickupAddress}
                                        disabled={status === Status.PENDING}
                                        onChange={(ev, newVal) => this.setState({ searchParams: { ...searchParams, pickupAddress: newVal } })}
                                    />
                                </Columns.Column>
                                <Columns.Column size="one-fifth">
                                    <TextField
                                        label={this.tKey('deliveryAddress')}
                                        placeholder={this.tKey('deliveryAddress')}
                                        value={searchParams.deliveryAddress}
                                        disabled={status === Status.PENDING}
                                        onChange={(ev, newVal) => this.setState({ searchParams: { ...searchParams, deliveryAddress: newVal } })}
                                    />
                                </Columns.Column>
                                <Columns.Column size="one-fifth">
                                    <TextField
                                        label={this.tKey('articleReferences')}
                                        placeholder={this.tKey('search')}
                                        disabled={status === Status.PENDING}
                                        value={searchParams.articleReferences}
                                        onChange={(ev, newVal) => this.setState({ searchParams: { ...searchParams, articleReferences: newVal } })}
                                    />
                                </Columns.Column>
                                {me.isAdmin && (
                                    <Columns.Column size="one-fifth">
                                        <FilteredVirtualCombobox
                                            label={this.tKey('client')}
                                            placeholder={this.tKey('select')}
                                            options={param.clients.reduce((acc, { key, text }) => {
                                                if (me.clientIds.length && !me.clientIds.includes(+key))
                                                    return acc
                                                return [...acc, { key, text: this.tObj(this.parseJson(text)) }]
                                            }, [])}
                                            selectedKey={searchParams.clientId}
                                            disabled={status === Status.PENDING}
                                            onChange={(_ev, option) => this.setState({
                                                searchParams: { ...searchParams, clientId: option.key },
                                            })}
                                        />
                                    </Columns.Column>
                                )}
                            </Columns>
                            <Columns>
                                <Columns.Column size="one-fifth">
                                    <div className={styles['date-filter']}>
                                        <DatePicker
                                            label={this.tKey('creationDate')}
                                            date={searchParams.creationDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        creationDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.creationDate}
                                            date={searchParams.creationDateEnd}
                                            minDate={searchParams.creationDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        creationDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                <Columns.Column size="one-fifth">
                                    <div className={styles['date-filter']}>
                                        <DatePicker
                                            label={this.tKey('pickupDate')}
                                            date={searchParams.pickupDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        pickupDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.pickupDate}
                                            date={searchParams.pickupDateEnd}
                                            minDate={searchParams.pickupDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        pickupDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                <Columns.Column size="one-fifth">
                                    <div className={styles['date-filter']}>
                                        <DatePicker
                                            label={this.tKey('deliveryDate')}
                                            date={searchParams.deliveryDate}
                                            minDate={DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        deliveryDate: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                        <DatePicker
                                            disabled={!searchParams.deliveryDate}
                                            date={searchParams.deliveryDateEnd}
                                            minDate={searchParams.deliveryDate || DateFilterMinDate}
                                            maxDate={DateFilterMaxDate}
                                            lang={lang}
                                            onChangeDate={date => {
                                                this.setState({
                                                    searchParams: {
                                                        ...searchParams,
                                                        deliveryDateEnd: this.isValidDate(date) ? date : null,
                                                    },
                                                })
                                            }}
                                        />
                                    </div>
                                </Columns.Column>
                                {['all', 'progress'].includes(match.params?.key) && (
                                    <Columns.Column size="one-fifth">
                                        <FilteredVirtualCombobox
                                            label={this.tKey('status')}
                                            placeholder={this.tKey('select')}
                                            options={param.workflowTransportSteps
                                                .filter(x => match.params?.key === 'all' || ![5, 7].includes(+x.key))
                                                .map(({ key, text }) => ({
                                                    key,
                                                    text: this.tObj(this.parseJson(text)),
                                                }))}
                                            selectedKey={searchParams.workflowTransportStepIds}
                                            disabled={status === Status.PENDING}
                                            onChange={(_ev, option) => this.setState({
                                                searchParams: {
                                                    ...searchParams,
                                                    workflowTransportStepIds: this.getUpdatedList(searchParams.workflowTransportStepIds, option),
                                                },
                                            })}
                                            multiSelect
                                        />
                                    </Columns.Column>
                                )}
                                <Columns.Column size="one-fifth">
                                    <div
                                        className={styles['action-filter-buttons']}
                                    >
                                        <div>
                                            <Label className="is-hidden-mobile">&nbsp;</Label>
                                            <DefaultButton
                                                text={this.tKey('research')}
                                                primary
                                                split
                                                disabled={status === Status.PENDING}
                                                onClick={() => this.submitInput.current.click()}
                                                iconProps={{ iconName: 'Search' }}
                                                menuProps={{
                                                    items: [
                                                        {
                                                            key: 'Search',
                                                            text: this.tKey('research'),
                                                            iconProps: { iconName: 'Search' },
                                                            onClick: () => this.submitInput.current.click(),
                                                        },
                                                        {
                                                            key: 'Export',
                                                            text: this.tKey('export'),
                                                            iconProps: { iconName: 'Export' },
                                                            onClick: () => this.export(),
                                                        },
                                                    ],
                                                }}
                                            />
                                        </div>
                                        <div className={styles.separator} />
                                        <div>
                                            <Label className="is-hidden-mobile">&nbsp;</Label>
                                            <TooltipHost
                                                content={this.tKey('resetFilters')}
                                                directionalHint={DirectionalHint.topAutoEdge}
                                                delay={TooltipDelay.medium}
                                                componentRef={this.toolTipRef}
                                            >
                                                <DefaultButton
                                                    disabled={status === Status.PENDING}
                                                    styles={{
                                                        root: {
                                                            minWidth: '40px',
                                                            maxWidth: '40px',
                                                        },
                                                    }}
                                                    iconProps={{ iconName: 'ClearFilter' }}
                                                    onClick={() => this.setState({
                                                        /** @type {SearchParamsType} */
                                                        searchParams: {
                                                            clientId: null,
                                                            references: '',
                                                            articleReferences: '',
                                                            pickupAddress: '',
                                                            deliveryAddress: '',
                                                            pickupDate: null,
                                                            pickupDateEnd: null,
                                                            workflowTransportStepIds: [],
                                                            creationDate: null,
                                                            creationDateEnd: null,
                                                            deliveryDate: null,
                                                            deliveryDateEnd: null,
                                                        },
                                                    })}
                                                />
                                            </TooltipHost>
                                        </div>
                                    </div>
                                </Columns.Column>
                            </Columns>
                            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                            <button
                                type="submit"
                                style={{ display: 'none' }}
                                ref={this.submitInput}
                                tabIndex={-1}
                            />
                        </form>
                    )}
                </Card>
                <br />
                <ContentResponsive min={768}>
                    <TransportList
                        lang={lang}
                        transports={items}
                        status={status}
                        onOrderByChange={orderBy => this.search(orderBy)}
                        workflowTransportSteps={param.workflowTransportSteps}
                        companyId={me.companyId}
                        me={me}
                        selectedTransports={selectedTransports}
                        onSelectionChange={newSelectedTransports => this.setState({ selectedTransports: newSelectedTransports })}
                        onDownloadSummary={() => this.downloadDocuments('summary')}
                        onDownloadLabels={() => this.setState({ isTransportLabelModalVisible: true })}
                        isDownloadingDocuments={documentStatus === Status.PENDING}
                    />
                </ContentResponsive>
                <ContentResponsive max={768}>
                    <TransportListCard
                        lang={lang}
                        transports={items}
                        status={status}
                    />
                </ContentResponsive>
                <TransportLabelModal
                    isVisible={isTransportLabelModalVisible}
                    setIsVisible={newVal => this.setState({ isTransportLabelModalVisible: newVal })}
                    lang={lang}
                    isDownloading={documentStatus === Status.PENDING}
                    onPrint={nbPerPage => this.downloadDocuments('labels', nbPerPage)}
                    defaultLabelFormat={defaultLabelFormat}
                />

                <Pagination
                    data={pagination}
                    lang={lang}
                    onPageChange={page => this.setState({ pagination: { ...pagination, currentPage: page } })}
                    itemName="transport"
                />
            </main>
        )
    }
}

IndexTransport.prototype.onColumnClick = onColumnClick
IndexTransport.prototype.flattenObj = flattenObj
IndexTransport.prototype.handleRenderColumn = handleRenderColumn
IndexTransport.prototype.time = time
IndexTransport.prototype.isValidDate = isValidDate
IndexTransport.prototype.parseJson = parseJson
IndexTransport.prototype.getUpdatedList = getUpdatedList
IndexTransport.prototype.tKey = tKey
IndexTransport.prototype.tObj = tObj
